"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useQueryParam = exports.useLocation = void 0;
exports.useGoBack = useGoBack;
exports.usePreviousRoutesSectionId = usePreviousRoutesSectionId;
exports.usePreviousRoutesSectionPath = usePreviousRoutesSectionPath;
exports.useSetDocumentMeta = useSetDocumentMeta;
exports.useGetPath = useGetPath;
exports.useGetPortalPath = useGetPortalPath;
exports.usePush = usePush;
exports.usePublicPortalDomain = usePublicPortalDomain;
exports.useIsPublicPortal = useIsPublicPortal;
exports.useIsPublicPortalEnabled = useIsPublicPortalEnabled;
const querystring = __importStar(require("querystring"));
const react_1 = __importDefault(require("react"));
const contexts_1 = require("../contexts");
/**
 * Goes a step back in the navigation history.
 *
 * @example
 *
 * ```tsx
 * import { useGoBack } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const goBack = useGoBack();
 *
 *   return <button onClick={() => goBack()}>Go back</button>;
 * };
 * ```
 *
 * @returns A function to go back in history
 */
function useGoBack() {
    const routerContext = react_1.default.useContext(contexts_1.RouterContext);
    return routerContext.goBack;
}
/**
 * Return the last navigated routes section. Routes are grouped by section, for example:
 * portal routes are grouped all together under the section id "assetPortal".
 *
 * @example
 *
 * ```tsx
 * import { usePreviousRoutesSectionId } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const group = usePreviousRoutesSectionId();
 *
 *   return `This route was navigated from ${group}`;
 * };
 * ```
 *
 * @returns The previous routes section in the navigation history
 */
function usePreviousRoutesSectionId() {
    var _a;
    const routerContext = react_1.default.useContext(contexts_1.RouterContext);
    const previousLocation = routerContext.getPreviousRoutesSection();
    return (_a = previousLocation === null || previousLocation === void 0 ? void 0 : previousLocation.id) !== null && _a !== void 0 ? _a : null;
}
/**
 * Return the last navigated path from the previous routes section. Routes are grouped by section, for example:
 * portal routes are grouped all together under the section id "assetPortal".
 *
 * @example
 *
 * ```tsx
 * import { usePreviousRoutesSectionPath } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const path = usePreviousRoutesSectionPath();
 *
 *   return <a href={path}>Back to last route</a>;
 * };
 * ```
 *
 * @returns The previous path from the last routes section in the navigation history
 */
function usePreviousRoutesSectionPath() {
    var _a;
    const routerContext = react_1.default.useContext(contexts_1.RouterContext);
    const previousLocation = routerContext.getPreviousRoutesSection();
    return (_a = previousLocation === null || previousLocation === void 0 ? void 0 : previousLocation.path) !== null && _a !== void 0 ? _a : null;
}
/**
 * Returns a function to set the HTML document metadata (title of the page, OG metadata, etc).
 *
 * @example
 *
 * ```tsx
 * import { useSetDocumentMeta } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const setDocumentMeta = useSetDocumentMeta();
 *
 *   setDocumentMeta({ title: 'My page' });
 * };
 * ```
 *
 * @returns A function to set the document meta
 */
function useSetDocumentMeta() {
    const routerContext = react_1.default.useContext(contexts_1.RouterContext);
    return routerContext.setDocumentMeta;
}
/**
 * Gets the path for an Exchange route.
 *
 * @example
 *
 * ```tsx
 * import { useGetPath } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const getPath = useGetPath();
 *   const pagePath = getPath('page', {
 *     pagePath: 'home',
 *     groupId: 'my-group-id',
 *     assetId: 'my-asset-id',
 *     minorVersion: '1.0',
 *     isDraft: false
 *   })
 *
 *   return <Link to={pagePath}>Go to home page</Link>;
 * };
 * ```
 *
 * @returns A function to get a path inside Exchange
 */
function useGetPath() {
    const routerContext = react_1.default.useContext(contexts_1.RouterContext);
    return routerContext.getPath;
}
/**
 * Gets a path inside an asset portal.
 *
 * @example
 *
 * ```tsx
 * import { useGetPortalPath } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const getPortalPath = useGetPortalPath();
 *   const path = getPortalPath('foo/bar'); // "https://anypoint.mulesoft.com/exchange/group-id/asset-id/minor/1.0/foo/bar/"
 *
 *   return <Link to={path}>Go to foo/bar</Link>;
 * };
 * ```
 *
 * @param isVersionNavigation Deprecated parameter used for legacy version navigation
 * @returns A function to get the path inside an asset portal
 */
function useGetPortalPath(isVersionNavigation = false) {
    const getPath = useGetPath();
    const { asset, isDraft } = react_1.default.useContext(contexts_1.AssetPortalContext);
    let query = '';
    if (asset) {
        let basePath = getPath('assetDetailBase', {
            groupId: asset.groupId,
            assetId: asset.assetId,
            version: asset.version,
            minorVersion: asset.minorVersion,
            versionGroup: asset.versionGroup,
            isVersionNavigation,
            isDraft
        });
        if (basePath.includes('?')) {
            [basePath, query] = basePath.split('?');
            query = `?${query}`;
        }
        return (path) => `${basePath}${path.startsWith('/') ? path.substring(1) : path}${path.endsWith('/') ? '' : '/'}${query}`;
    }
    return () => '';
}
/**
 * Manually navigates to a new route.
 *
 * @example
 *
 * ```tsx
 * import { usePush } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const push = usePush();
 *
 *   return <button onClick={() => push('/my-route')}>Go to my route</button>;
 * };
 * ```
 *
 * @returns A function to set a new URL in the history
 */
function usePush() {
    const routerContext = react_1.default.useContext(contexts_1.RouterContext);
    return routerContext.push;
}
/**
 * Returns public portal domain, when navigating one.
 *
 * @example
 *
 * ```tsx
 * import { usePublicPortalDomain } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const domain = usePublicPortalDomain();
 *
 *   ...
 * };
 * ```
 *
 * @returns The public portal domain
 */
function usePublicPortalDomain() {
    const { context: routingContext } = react_1.default.useContext(contexts_1.RouterContext);
    return routingContext === null || routingContext === void 0 ? void 0 : routingContext.publicPortalDomain;
}
/**
 * Returns `true` if Exchange is navigating inside a public portal.
 *
 * @example
 *
 * ```tsx
 * import { useIsPublicPortal } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const isPublicPortal = useIsPublicPortal();
 *
 *   ...
 * };
 * ```
 *
 * @returns A boolean that will be true if the user is navigating a public portal
 */
function useIsPublicPortal() {
    const publicPortalDomain = usePublicPortalDomain();
    return !!publicPortalDomain;
}
/**
 * Returns `true` if the public portal from the user's profile is enabled.
 *
 * @example
 *
 * ```tsx
 * import { useIsPublicPortalEnabled } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const isPublicPortalEnabled = useIsPublicPortalEnabled();
 *
 *   ...
 * };
 * ```
 *
 * @returns A boolean that will be true if the public portal from the user's profile is enabled
 */
function useIsPublicPortalEnabled() {
    const { context: routingContext } = react_1.default.useContext(contexts_1.RouterContext);
    return routingContext.publicPortalStatus === 'enabled';
}
/**
 * Returns the current location for the application. This will typically be the `Location` object of the document
 *
 * @returns `Location` object with information about the current location of the document
 */
const useLocation = () => {
    const { location } = react_1.default.useContext(contexts_1.RouterContext);
    return location;
};
exports.useLocation = useLocation;
const getParser = (defaultValue) => {
    if (defaultValue === null || defaultValue === undefined) {
        return (value) => value;
    }
    if (typeof defaultValue === 'string') {
        return (value) => value;
    }
    if (Array.isArray(defaultValue)) {
        return (value) => {
            if (Array.isArray(value)) {
                return value;
            }
            if (value === undefined || value === null) {
                return defaultValue;
            }
            return [value];
        };
    }
    if (typeof defaultValue === 'boolean' || typeof defaultValue === 'number') {
        return (value) => {
            if (value === undefined || value === null) {
                return defaultValue;
            }
            return JSON.parse(value);
        };
    }
    throw new Error('Invalid query param type');
};
/**
 * Utility for managing query params in the URL. Works similar to React.useState
 *
 * @example
 *
 * ```tsx
 * import { useQueryParam } from '@mulesoft/exchange-react-hooks';
 *
 * const MyComponent = () => {
 *   const [myParam, setMyParam] = useQueryParam('my-param', 'default');
 *
 *   // something happens
 *   setMyParam('new-param');
 *
 *   console.log(myParam);
 *
 *   ...
 * };
 * ```
 *
 * @param param Key for the query param in the URL
 * @param defaultValue The default value used in case the query param is not defined. Used also to define if the query param is an array. For example: use `useQueryParam('organizationId', [])` for an array of "organization Ids"
 * @returns A tuple for the actual value of the query param and a function to set a new param. Similar to `React.useState` format.
 */
const useQueryParam = (param, defaultValue) => {
    const location = (0, exports.useLocation)();
    const push = usePush();
    const query = react_1.default.useMemo(() => querystring.parse(location.search.replace('?', '')), [location.search]);
    const queryParam = react_1.default.useMemo(() => {
        var _a;
        const parseQueryParam = getParser(defaultValue);
        return (_a = parseQueryParam(query[param])) !== null && _a !== void 0 ? _a : defaultValue;
    }, [query, defaultValue]);
    const setQueryParam = react_1.default.useCallback((newValue) => {
        const newQueryString = querystring.stringify(Object.assign(Object.assign({}, query), { [param]: newValue }));
        const newLocation = `${location.pathname}?${newQueryString}`;
        push(newLocation);
    }, [query, location.search]);
    return [queryParam, setQueryParam];
};
exports.useQueryParam = useQueryParam;
